import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  useAvoidFirstRender,
  useHorizontalSwipe,
  useOutsideClickEffect,
} from '@saviynt/common';
import { Box, Loader, useScrollbarWidth } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import CarouselHeader from './CarouselHeader';
import CarouselSkeleton from './CarouselSkeleton';
import {
  CAROUSEL_CARDS,
  SYSTEM_DATA_THEMES,
  testIds,
  timers,
} from './constant';
import { throttle } from './utils';

import './Carousel.css';

// Do not change this counter
const DEFAULT_COUNT_OF_CARDS = 5;
const MAX_TRAVEL_FOR_SWIPE_HANDLE = 80; // 80px is the max value we can have
const RANGE_FOR_LEFT_PROPERTY_SWIPE = 30;
const DEFAULT_LEFT_PROPERTY_VALUE_FOR_CARD = 50;

function Carousel({
  isOpen,
  onClose,
  isLoading,
  header,
  headerClassName,
  layout,
  layoutClassName,
  footer,
  footerClassName,
  isPrevDisabled,
  isNextDisabled,
  onClickNext,
  onClickPrev,
  breadCrumb,
  breadCrumbOnClick,
  isCloseOnOutsideClick,
  isSwipeDisabled,
  isInBackground,
  dataTheme,
  className,
  dataTestId,
}) {
  const carouselRef = useRef(null);
  const overlayRef = useRef(null);

  /** focusOnlyModalIslandElements makes the tab order locked to only the focusable components inside the modalIsland */
  const focusOnlyModalIslandElements = (e) => {
    if (!carouselRef.current) return;

    const focusableElements = carouselRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.keyCode === 9) {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  // Get the browser scrollBar width
  useScrollbarWidth();

  const overlayBgClasses = classnames(
    'Carousel-background',
    isOpen && 'Carousel-background--isOpen'
  );
  const carouselClasses = classnames(
    'Carousel',
    isOpen && 'Carousel--isOpen',
    className
  );
  const carouselSectionClasses = classnames('Carousel-section');
  const layoutClasses = classnames('Carousel-layout', layoutClassName);
  const footerClasses = classnames('Carousel-footer', footerClassName);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('Carousel--isOpen--bodyScrollLock');
      document.addEventListener('keydown', focusOnlyModalIslandElements);
      const closeButton = document.getElementById('modalCloseButton-true');

      closeButton?.focus();
    } else {
      document.body.classList.remove('Carousel--isOpen--bodyScrollLock');
    }

    return () => {
      document.body.classList.remove('Carousel--isOpen--bodyScrollLock');
      document.removeEventListener('keydown', focusOnlyModalIslandElements);
    };
  }, [isOpen]);

  // Used to get the final height of the container the set it to 0 for the transition animation to play
  useEffect(() => {
    const modalCurrent = carouselRef.current;
    const overlayCurrent = overlayRef.current;

    if (!modalCurrent && !overlayCurrent) return;

    if (isOpen) {
      setTimeout(() => {
        modalCurrent.style.opacity = 1;
        modalCurrent.style.pointerEvents = 'all';
        overlayCurrent.style.opacity = 1;
        overlayCurrent.style.pointerEvents = 'all';
      }, timers.CAROUSEL_MENU_CLOSE_TRANSITION_DELAY_MS);
    }
  }, [isOpen]);

  const toggleCarouselOpen = useCallback(() => {
    if (isOpen) {
      const modalCurrent = carouselRef.current;
      const overlayCurrent = overlayRef.current;

      if (modalCurrent && overlayCurrent) {
        setTimeout(() => {
          modalCurrent.style.opacity = 0;
          modalCurrent.style.pointerEvents = 'none';
          overlayCurrent.style.opacity = 0;
          overlayCurrent.style.pointerEvents = 'none';
        }, timers.CAROUSEL_MENU_CLOSE_TRANSITION_DELAY_MS);
        setTimeout(() => {
          onClose(false);
        }, timers.CAROUSEL_CLOSE_DELAY);
      }
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    const modalCurrent = carouselRef.current;
    const overlayCurrent = overlayRef.current;

    if (!modalCurrent || !overlayCurrent) return;

    if (isInBackground) {
      modalCurrent.style.pointerEvents = 'none';
      overlayCurrent.style.opacity = 0;

      return;
    }

    overlayCurrent.style.opacity = 1;
    modalCurrent.style.pointerEvents = 'all';
  }, [isInBackground]);

  const closeOnEscapeKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && !isInBackground) toggleCarouselOpen();
    },
    [toggleCarouselOpen]
  );

  useEffect(() => {
    document.body.addEventListener('keydown', closeOnEscapeKeyDown);

    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, [closeOnEscapeKeyDown]);

  useOutsideClickEffect(
    carouselRef,
    () => toggleCarouselOpen(),
    isOpen && isCloseOnOutsideClick
  );

  // Code for cards' management
  const [currentIndex, setCurrentIndex] = useState(2);
  const [visibleCards, setVisibleCards] = useState([
    0,
    1,
    DEFAULT_COUNT_OF_CARDS - 1,
  ]);

  // TODO: Sagar to handle delayed offloading of side cards which is not working currently
  useEffect(() => {
    const sideCardDelay = setTimeout(() => {
      setVisibleCards([
        (currentIndex - 1 + DEFAULT_COUNT_OF_CARDS) % DEFAULT_COUNT_OF_CARDS,
        currentIndex,
        (currentIndex + 1) % DEFAULT_COUNT_OF_CARDS,
      ]);
    }, timers.CAROUSEL_TRANSITION_DELAY);

    return () => clearTimeout(sideCardDelay);
  }, [currentIndex]);

  // Code for cards' click events
  const handleNextClick = () => {
    setCurrentIndex((prev) => (prev + 1) % DEFAULT_COUNT_OF_CARDS);

    if (typeof onClickNext === 'function') {
      onClickNext();
    }
  };

  const handlePrevClick = (shouldSwipeCardOnly = false) => {
    setCurrentIndex((prev) => (prev - 1) % DEFAULT_COUNT_OF_CARDS);

    if (typeof onClickPrev === 'function' && !shouldSwipeCardOnly) {
      onClickPrev();
    }
  };

  const handleClick = (event) => {
    const targetClass = event.target.className;

    if (
      typeof targetClass === 'string' &&
      targetClass?.includes(CAROUSEL_CARDS.PREV1) &&
      !isPrevDisabled
    ) {
      event.preventDefault();
      handlePrevClick();
    }

    if (
      typeof targetClass === 'string' &&
      targetClass?.includes(CAROUSEL_CARDS.NEXT1) &&
      !isNextDisabled
    ) {
      event.preventDefault();
      handleNextClick();
    }
  };

  const handleBreadCrumbItemClick = (level) => {
    handlePrevClick(true);
    breadCrumbOnClick(level);
  };

  // Code for the swipe animation
  const [defaultLeftValue, setDefaultLeftValue] = useState(
    DEFAULT_LEFT_PROPERTY_VALUE_FOR_CARD
  );

  const addDefaultLeft = () => {
    const cards = document.querySelectorAll('.Carousel li');

    cards.forEach((card) => {
      card.classList.add('Carousel-cards--hardLeft');
    });

    setDefaultLeftValue(50);
  };

  const removeDefaultLeft = () => {
    const cards = document.querySelectorAll('.Carousel li');

    cards.forEach((card) => {
      card.classList.remove('Carousel-cards--hardLeft');
    });
  };

  const swipeHandle = useHorizontalSwipe(
    {
      onSwipedLeft: () => (!isNextDisabled ? handleNextClick() : () => {}),
      onSwipedRight: () => (!isPrevDisabled ? handlePrevClick() : () => {}),
      onEventStart: () => removeDefaultLeft(),
      onEventStop: () => addDefaultLeft(),
    },
    80
  );

  const swipeDirection = swipeHandle.displacement.direction;
  const swipeTravel = swipeHandle.boundedDistance;

  useAvoidFirstRender(() => {
    const swipeTravelMapped = Math.floor(
      (RANGE_FOR_LEFT_PROPERTY_SWIPE / MAX_TRAVEL_FOR_SWIPE_HANDLE) *
        swipeTravel *
        0.9
    );

    const leftValue =
      swipeDirection === 'R'
        ? DEFAULT_LEFT_PROPERTY_VALUE_FOR_CARD + swipeTravelMapped
        : DEFAULT_LEFT_PROPERTY_VALUE_FOR_CARD - swipeTravelMapped;

    throttle(() => setDefaultLeftValue(leftValue), swipeTravel, 5);
  }, [swipeDirection, swipeTravel]);

  // Code for the layout, header and footer
  const getClassNameForCards = (index) => {
    const position =
      (index - currentIndex + DEFAULT_COUNT_OF_CARDS) % DEFAULT_COUNT_OF_CARDS;

    switch (position) {
      case 0:
        return CAROUSEL_CARDS.MID;
      case 1:
        return `${CAROUSEL_CARDS.NEXT1} ${
          isNextDisabled ? 'forward_1--isDisabled' : ''
        }`;
      case 2:
        return CAROUSEL_CARDS.NEXT2;
      case DEFAULT_COUNT_OF_CARDS - 1:
        return `${CAROUSEL_CARDS.PREV1} ${
          isPrevDisabled ? 'previous_1--isDisabled' : ''
        }`;
      case DEFAULT_COUNT_OF_CARDS - 2:
        return CAROUSEL_CARDS.PREV2;
      default:
        return '';
    }
  };

  const renderModalSections = () => {
    if (!isLoading) {
      return (
        <Box tag='section' className={carouselSectionClasses}>
          <CarouselHeader
            header={header}
            headerClassName={headerClassName}
            breadCrumb={breadCrumb}
            breadCrumbOnClick={handleBreadCrumbItemClick}
            onClick={toggleCarouselOpen}
            dataTestId={testIds.CAROUSEL_CLOSE_BUTTON}
          />
          <div className={layoutClasses}>{layout}</div>
          <div className={footerClasses}>{footer}</div>
        </Box>
      );
    }

    return (
      <Box
        tag='section'
        className={`${carouselSectionClasses} Carousel-section--isLoading`}>
        <Loader
          kind='loop'
          format='main'
          color='brand'
          size='large'
          dataTestId='Carousel-loadingTestId'
        />
      </Box>
    );
  };

  const getModalContent = () =>
    Object.values(CAROUSEL_CARDS)?.map((item, index) => {
      const isClassForMidSection = Boolean(
        getClassNameForCards(index) === CAROUSEL_CARDS.MID
      );

      const middleCardTouchProps = {
        onTouchStart: swipeHandle.handlers.onTouchStart,
        onTouchMove: swipeHandle.handlers.onTouchMove,
        onTouchEnd: swipeHandle.handlers.onTouchEnd,
      };

      return (
        <li
          key={item}
          style={{ left: `${defaultLeftValue}%` }}
          className={`${getClassNameForCards(index)} Carousel-cards--hardLeft`}
          onClick={(e) => handleClick(e)}
          onKeyDown={() => {}}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(isClassForMidSection && !isSwipeDisabled
            ? middleCardTouchProps
            : {})}
          role='presentation'>
          {isClassForMidSection
            ? renderModalSections()
            : visibleCards.includes(index) && <CarouselSkeleton />}
        </li>
      );
    });

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <Box tag='div' ref={overlayRef} className={overlayBgClasses} />
      <Box
        tag='ul'
        ref={carouselRef}
        id='Carousel'
        className={carouselClasses}
        dataTestId={dataTestId}
        dataTheme={dataTheme}>
        {getModalContent()}
      </Box>
    </>,
    document.body
  );
}

Carousel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  header: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
  }).isRequired,
  headerClassName: PropTypes.string,
  layout: PropTypes.element.isRequired,
  layoutClassName: PropTypes.string,
  footer: PropTypes.element.isRequired,
  footerClassName: PropTypes.string,
  isPrevDisabled: PropTypes.bool,
  isNextDisabled: PropTypes.bool,
  onClickPrev: PropTypes.func,
  onClickNext: PropTypes.func,
  breadCrumb: PropTypes.arrayOf(PropTypes.shape({})),
  breadCrumbOnClick: PropTypes.func,
  isCloseOnOutsideClick: PropTypes.bool,
  isSwipeDisabled: PropTypes.bool,
  isInBackground: PropTypes.bool,
  dataTheme: PropTypes.oneOf(Object.values(SYSTEM_DATA_THEMES)),
  className: PropTypes.string,
  dataTestId: PropTypes.string,
};

Carousel.defaultProps = {
  isLoading: false,
  headerClassName: '',
  layoutClassName: '',
  footerClassName: '',
  isPrevDisabled: false,
  isNextDisabled: false,
  onClickPrev: () => {},
  onClickNext: () => {},
  breadCrumb: [],
  breadCrumbOnClick: () => {},
  isCloseOnOutsideClick: false,
  isSwipeDisabled: false,
  isInBackground: false,
  dataTheme: null,
  className: null,
  dataTestId: null,
};

export default Carousel;
